import type { LazyQueryHookOptions } from '@apollo/react-hooks'
import type { SearchCompaniesQuery } from '@paintscout/api'
import { useSearchCompaniesLazyQuery } from '@paintscout/api'
import { useState } from 'react'

/**
 * A helper hook to simplify managing company search and pagination
 */
export function useCompanySearch(
  options: { query?: string; fetchPolicy?: string; limit?: number; prevBookmark?: string; prevSort?: string[] } & Omit<
    LazyQueryHookOptions,
    'query'
  > = {}
) {
  const { limit, query, fetchPolicy = 'cache-first', prevBookmark, prevSort, ...queryOptions } = options
  const [bookmark, setBookmark] = useState<string>(prevBookmark ?? undefined)
  const [lastQuery, setLastQuery] = useState(query)
  const [sort, setSort] = useState<string[]>(prevSort ?? [])

  const [searchCompanies, queryResults] = useSearchCompaniesLazyQuery({
    fetchPolicy,
    ...(queryOptions as any), // ? this ts error is stupid
    onCompleted: (data) => {
      if (data?.searchCompanies) {
        setBookmark(data.searchCompanies.bookmark)
      }
      if (queryOptions.onCompleted) {
        queryOptions.onCompleted(data)
      }
    }
  })

  const search = ({ ...args }: { query: string; limit?: number; sort?: string[] }) => {
    setLastQuery(args.query)

    if (args.sort) {
      setSort(args.sort)
    }

    searchCompanies({
      variables: {
        limit,
        ...args,
        sort: args.sort || sort
      }
    })
  }

  const fetchMore = () => {
    return queryResults.fetchMore({
      variables: { query: lastQuery, sort, limit, bookmark },
      updateQuery: (previousResult: SearchCompaniesQuery, { fetchMoreResult }) => {
        if (!fetchMoreResult || !fetchMoreResult.searchCompanies) {
          return previousResult
        }

        return {
          ...previousResult,
          ...fetchMoreResult,
          searchCompanies: {
            ...previousResult.searchCompanies,
            ...fetchMoreResult.searchCompanies,
            bookmark: fetchMoreResult.searchCompanies.bookmark,
            quotes: [...previousResult.searchCompanies.companies, ...fetchMoreResult.searchCompanies.companies]
          }
        }
      }
    })
  }

  const newQueryResults = { ...queryResults, fetchMore }

  return [search, newQueryResults] as [typeof search, typeof newQueryResults]
}
